import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPI = [
  "Matrimonio / Wedding",
  "Festa privata (compleanno, laurea, anniversario)",
  "Evento aziendale",
  "Evento pubblico / manifestazione",
  "Altro",
];
const INVITATI = ["Fino a 50", "51-100", "101-200", "Oltre 200", "Da definire"];
const DURATA = ["Fino a 3 ore", "3-5 ore", "6-8 ore", "Tutta la giornata", "Da definire"];
const BEVANDE = ["Spritz", "Campari", "Negroni", "Gin Tonic", "Gin Lemon", "Birra", "Vino / Prosecco", "Analcolici", "Da definire insieme"];
const ALLESTIMENTI = ["Originale Sprizziamoci", "Wedding elegante"];
const CONTATTO = ["Telefono", "WhatsApp", "Email"];

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-2.5">
    {children}
  </label>
);

export default function QuoteForm({ initialTipo = "" }) {
  const [form, setForm] = useState({
    nome_cognome: "",
    email: "",
    telefono: "",
    tipologia_evento: initialTipo || "",
    data_evento: "",
    comune: "",
    invitati: "",
    durata: "",
    bevande: [],
    allestimento: "",
    richieste: "",
    preferenza_contatto: "",
    website: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleBevanda = (b) =>
    setForm((f) => ({
      ...f,
      bevande: f.bevande.includes(b) ? f.bevande.filter((x) => x !== b) : [...f.bevande, b],
    }));

  const validate = () => {
    const e = {};
    if (form.nome_cognome.trim().length < 2) e.nome_cognome = "Inserisci nome e cognome";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Inserisci un indirizzo email valido";
    if (form.telefono.replace(/\D/g, "").length < 8) e.telefono = "Inserisci un numero di telefono valido";
    if (!form.tipologia_evento) e.tipologia_evento = "Seleziona la tipologia di evento";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Controlla i campi evidenziati");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quote-requests`, form, { timeout: 15000 });
      setDone(true);
      toast.success("Richiesta inviata. Ti ricontattiamo al più presto!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Invio non riuscito. Riprova o scrivici su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  if (done)
    return (
      <div
        data-testid="quote-success-panel"
        className="rounded-3xl border border-brand-orange/40 bg-brand-orange/[0.06] p-10 md:p-14 text-center"
      >
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-orange text-brand-ink">
          <Check size={26} strokeWidth={3} />
        </span>
        <h3 className="mt-6 font-display text-4xl font-semibold">Richiesta inviata!</h3>
        <p className="mt-4 text-sm leading-relaxed text-white/60 max-w-md mx-auto">
          Abbiamo ricevuto la tua richiesta: ti ricontattiamo per definire bevande, allestimento
          e ogni dettaglio del servizio. Il prossimo brindisi è tuo.
        </p>
      </div>
    );

  return (
    <form onSubmit={submit} noValidate data-testid="quote-form" className="space-y-8">
      <div className="grid gap-8 md:grid-cols-2">
        <div>
          <Label htmlFor="q-nome">Nome e cognome *</Label>
          <input
            id="q-nome"
            data-testid="quote-field-nome"
            className={`field ${errors.nome_cognome ? "border-red-500/70" : ""}`}
            placeholder="Mario Rossi"
            value={form.nome_cognome}
            onChange={(e) => set("nome_cognome", e.target.value)}
          />
          {errors.nome_cognome && <p className="mt-2 text-xs text-red-400">{errors.nome_cognome}</p>}
        </div>
        <div>
          <Label htmlFor="q-email">Email *</Label>
          <input
            id="q-email"
            type="email"
            data-testid="quote-field-email"
            className={`field ${errors.email ? "border-red-500/70" : ""}`}
            placeholder="nome@esempio.it"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
          {errors.email && <p className="mt-2 text-xs text-red-400">{errors.email}</p>}
        </div>
        <div>
          <Label htmlFor="q-telefono">Numero di telefono *</Label>
          <input
            id="q-telefono"
            type="tel"
            data-testid="quote-field-telefono"
            className={`field ${errors.telefono ? "border-red-500/70" : ""}`}
            placeholder="+39 000 000 0000"
            value={form.telefono}
            onChange={(e) => set("telefono", e.target.value)}
          />
          {errors.telefono && <p className="mt-2 text-xs text-red-400">{errors.telefono}</p>}
        </div>
        <div>
          <Label htmlFor="q-tipo">Tipologia di evento *</Label>
          <select
            id="q-tipo"
            data-testid="quote-field-tipologia"
            className={`field appearance-none ${errors.tipologia_evento ? "border-red-500/70" : ""} ${form.tipologia_evento ? "" : "text-white/30"}`}
            value={form.tipologia_evento}
            onChange={(e) => set("tipologia_evento", e.target.value)}
          >
            <option value="">Seleziona…</option>
            {TIPI.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {errors.tipologia_evento && <p className="mt-2 text-xs text-red-400">{errors.tipologia_evento}</p>}
        </div>
        <div>
          <Label htmlFor="q-data">Data dell'evento</Label>
          <input
            id="q-data"
            type="date"
            data-testid="quote-field-data"
            className="field [color-scheme:dark]"
            value={form.data_evento}
            onChange={(e) => set("data_evento", e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="q-comune">Comune o località</Label>
          <input
            id="q-comune"
            data-testid="quote-field-comune"
            className="field"
            placeholder="Dove si svolge la festa"
            value={form.comune}
            onChange={(e) => set("comune", e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="q-invitati">Numero indicativo di invitati</Label>
          <select
            id="q-invitati"
            data-testid="quote-field-invitati"
            className={`field appearance-none ${form.invitati ? "" : "text-white/30"}`}
            value={form.invitati}
            onChange={(e) => set("invitati", e.target.value)}
          >
            <option value="">Seleziona…</option>
            {INVITATI.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="q-durata">Durata desiderata del servizio</Label>
          <select
            id="q-durata"
            data-testid="quote-field-durata"
            className={`field appearance-none ${form.durata ? "" : "text-white/30"}`}
            value={form.durata}
            onChange={(e) => set("durata", e.target.value)}
          >
            <option value="">Seleziona…</option>
            {DURATA.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <Label>Cocktail o bevande richiesti</Label>
        <div className="flex flex-wrap gap-2.5" data-testid="quote-bevande-group">
          {BEVANDE.map((b) => (
            <button
              type="button"
              key={b}
              data-testid={`quote-bevanda-${b.toLowerCase().replace(/\s|\/|:/g, "-").replace(/[^a-z-]/g, "")}`}
              onClick={() => toggleBevanda(b)}
              className={`chip ${form.bevande.includes(b) ? "chip-active" : ""}`}
            >
              {b}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label>Tipologia di allestimento</Label>
        <div className="flex flex-wrap gap-2.5" data-testid="quote-allestimento-group">
          {ALLESTIMENTI.map((a) => (
            <button
              type="button"
              key={a}
              data-testid={`quote-allestimento-${a.toLowerCase().split(" ")[0]}`}
              onClick={() => set("allestimento", form.allestimento === a ? "" : a)}
              className={`chip ${form.allestimento === a ? "chip-active" : ""}`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="q-richieste">Eventuali richieste aggiuntive</Label>
        <textarea
          id="q-richieste"
          data-testid="quote-field-richieste"
          rows={4}
          className="field resize-none"
          placeholder="Raccontaci la tua idea: portaci la tua idea, al brindisi pensiamo insieme."
          value={form.richieste}
          onChange={(e) => set("richieste", e.target.value)}
        />
      </div>

      <div>
        <Label>Preferenza di contatto</Label>
        <div className="flex flex-wrap gap-2.5" data-testid="quote-contatto-group">
          {CONTATTO.map((c) => (
            <button
              type="button"
              key={c}
              data-testid={`quote-contatto-${c.toLowerCase()}`}
              onClick={() => set("preferenza_contatto", form.preferenza_contatto === c ? "" : c)}
              className={`chip ${form.preferenza_contatto === c ? "chip-active" : ""}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Honeypot antispam: invisibile per le persone */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={form.website}
        onChange={(e) => set("website", e.target.value)}
        className="hidden"
      />

      <button
        type="submit"
        data-testid="quote-submit"
        disabled={sending}
        className="group inline-flex w-full md:w-auto items-center justify-center gap-3 rounded-full bg-brand-orange px-10 py-5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink shadow-[0_0_50px_rgba(255,121,0,0.3)] transition-colors duration-300 hover:bg-[#ff9433] disabled:opacity-60"
      >
        {sending ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Invio in corso…
          </>
        ) : (
          <>
            Richiedi il tuo preventivo
            <ArrowRight size={15} strokeWidth={2.4} className="transition-transform duration-300 group-hover:translate-x-1" />
          </>
        )}
      </button>
      <p className="text-xs leading-relaxed text-white/40 max-w-lg">
        Inviando accetti di essere ricontattato per ricevere il preventivo. Nessun prezzo fisso:
        il preventivo viene costruito sulla base del tuo evento.
      </p>
    </form>
  );
}
